/**
 * Unsaved-changes guard.
 *
 * Two layers:
 *  - Browser-level: a `beforeunload` listener triggers the native "Leave
 *    site?" prompt on tab close / refresh / external navigation. The
 *    user's actual changes can't be inlined into that prompt — browsers
 *    show their own generic message — but the warning IS the value.
 *  - In-app: `confirmIfDirty(action)` wraps any callback that would lose
 *    state (back-to-projects, mode switch). Shows a styled modal when
 *    the form is dirty. The wrapper is sync — the async modal flow
 *    runs internally and triggers the action only when the user
 *    confirms.
 */

import { useEffect } from 'react';

import { confirm, confirmThreeWay } from '../components/ConfirmDialog.js';
import { toast } from '../components/Toast.js';
import { APPLY_BLOCKED_TOAST } from '../messages.js';
import type { Store } from '../store.js';

export function useUnsavedChangesWarning(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      // Some browsers respect preventDefault; others want returnValue;
      // belt-and-braces both.
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
}

/**
 * Wrap an in-app navigation action with a confirmation prompt.
 *
 * Three distinct conditions can trigger a prompt:
 *  - `hasDraft`: an in-flight sub-record is being authored. The user
 *    is offered Apply / Discard / Cancel — Apply commits the draft
 *    AND runs the action; Discard rolls back AND runs the action;
 *    Cancel does nothing. If Apply fails (validation issues), we
 *    surface a toast and stay put.
 *  - `dirty`: the document has unsaved edits but no active draft.
 *    Standard "discard changes" confirm.
 *  - Neither: no prompt; action fires immediately.
 *
 * The draft case takes precedence over plain dirty (a draft IS a
 * kind of dirty, but the resolution path is different).
 */
export function confirmIfDirty(
  dirty: boolean,
  action: () => void,
  options?: { hasDraft?: boolean; store?: Store; draftKind?: 'create' | 'edit' | null },
): () => void {
  return () => {
    const hasDraft = options?.hasDraft === true;
    if (!dirty && !hasDraft) {
      action();
      return;
    }
    if (hasDraft && options?.store) {
      const store = options.store;
      // If the draft has no actual user edits (the user zoomed in but
      // didn't touch anything), silently clear it and proceed —
      // otherwise we'd nag the user about "unapplied changes" that
      // don't exist. We still need to fall through to the dirty
      // branch below in case there are previously-committed-but-
      // unsaved edits at the document level.
      if (!store.draftHasChanges()) {
        store.clearDraft();
        // Fall through to the dirty check below — there may be
        // committed-but-unsaved changes that still warrant a prompt.
        if (!dirty) {
          action();
          return;
        }
        // dirty is true, hasDraft is now effectively false → use the
        // plain dirty-confirm path.
      } else {
        const isCreate = options.draftKind === 'create';
        void confirmThreeWay({
          title: isCreate ? 'New item not yet applied' : 'Unapplied changes',
          message: isCreate
            ? 'Apply the new item to keep it, or discard it before leaving.'
            : 'Apply your edits to keep them, or discard them before leaving.',
          primaryLabel: 'Apply',
          secondaryLabel: 'Discard',
          secondaryDanger: true,
        }).then((choice) => {
          if (choice === 'cancel') return;
          if (choice === 'primary') {
            const ok = store.applyDraft();
            if (!ok) {
              toast(APPLY_BLOCKED_TOAST, 'error');
              return;
            }
          } else {
            // 'secondary' = Discard
            store.discardDraft();
          }
          action();
        });
        return;
      }
    }
    void confirm({
      title: 'Discard unsaved changes?',
      message: 'Your edits will be lost if you leave now. Save first if you want to keep them.',
      confirmLabel: 'Discard and leave',
      cancelLabel: 'Keep editing',
      danger: true,
    }).then((ok) => {
      if (ok) action();
    });
  };
}
